import moment from 'moment';

export const EVENT_TOOLTIP_FONT_FAMILY = '"GT Walsheim Pro", "Open Sans", sans-serif';

const escapeHtml = (value = '') =>
    String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const getEventBadgeBackground = (color) => `${color}1A`;
const EVENT_REASON_PREVIEW_LIMIT = 140;

const stripHtml = (value = '') =>
    String(value)
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const truncateText = (value = '', limit = EVENT_REASON_PREVIEW_LIMIT) => {
    if (value.length <= limit) return value;
    return `${value.slice(0, limit).trimEnd()}...`;
};

export const formatInsightDate = (value, monthFormat = 'MMM', dayFormat = 'DD MMM') => {
    const parsed = moment(value, ['YYYY-MM-DD', 'YYYY-MM', 'GGGG-[W]WW'], true);
    if (!parsed.isValid()) return value;
    if (value?.includes('-W')) {
        return parsed.startOf('isoWeek').format(dayFormat);
    }
    return parsed.format(value?.length > 7 ? dayFormat : monthFormat);
};

export const toCategoryKey = (value, categories = []) => {
    const parsed = moment(value);
    if (!parsed.isValid() || !categories.length) return value;
    const sample = categories[0];
    if (sample?.includes('-W')) return parsed.format('GGGG-[W]WW');
    if (sample?.length > 7) return parsed.format('YYYY-MM-DD');
    return parsed.format('YYYY-MM');
};

export const getEventLineColor = (type) => {
    switch (type) {
        case 'Client':
            return '#0C66E4';
        case 'Target':
            return '#FFC051';
        case 'Goal':
        default:
            return '#2A8E6D';
    }
};

const getBucketColor = (events = []) => {
    const types = [...new Set(events.map((event) => event?.type).filter(Boolean))];
    if (types.length === 1) return getEventLineColor(types[0]);
    if (types.includes('Goal')) return getEventLineColor('Goal');
    if (types.includes('Client')) return getEventLineColor('Client');
    if (types.includes('Target')) return getEventLineColor('Target');
    return '#667085';
};

export const buildEventBuckets = (events = [], categories = []) => {
    if (!events.length || !categories.length) return [];

    const bucketMap = new Map();

    events.forEach((event) => {
        const key = toCategoryKey(event?.date, categories);
        if (!key || !categories.includes(key)) return;

        const bucket = bucketMap.get(key) || { key, events: [] };
        bucket.events.push(event);
        bucketMap.set(key, bucket);
    });

    return categories
        .filter((category) => bucketMap.has(category))
        .map((category) => {
            const bucket = bucketMap.get(category);
            return {
                ...bucket,
                count: bucket.events.length,
                color: getBucketColor(bucket.events),
            };
        });
};

const buildBucketEventItemHtml = (event) => {
    const color = getEventLineColor(event?.type);
    const badgeLabel = escapeHtml(event?.type || 'Event');
    const staff = escapeHtml(event?.staff || 'system');
    const reason = escapeHtml(truncateText(stripHtml(event?.label || 'No reason provided')));
    const date = escapeHtml(moment.utc(event?.date).format('MMM DD/YYYY'));

    return `
        <div style="padding:8px 10px; background:white; border-radius:6px; box-shadow:0 0 6px rgba(0,0,0,0.15);">
            <div style="display:flex; align-items:center; justify-content:space-between; gap:16px; width:100%;">
                <p style="color:#0C66E4; font-size:12px; font-weight:600; line-height:18px; margin:0 0 8px;">${date}</p>
                <div style="background:${getEventBadgeBackground(color)}; color:${color}; padding:2px 8px; border-radius:16px; font-weight:600; font-size:12px;">
                    ${badgeLabel}
                </div>
            </div>
            <p style="font-size:12px; color:#344054; margin:0 0 4px;">Staff: <b style="font-weight:600;">${staff}</b></p>
            <div style="font-size:12px; color:#344054; margin:0; display:flex; white-space:normal; word-break:break-word; overflow-wrap:break-word; max-width:500px;">
                <span style="margin-right:2px; white-space:nowrap;">Reason:</span>
                <span style="font-weight:600;">${reason}</span>
            </div>
        </div>
    `;
};

export const buildBucketTooltipHtml = (bucket) => {
    if (!bucket?.events?.length) return '';

    return `
        <div style="display:flex; flex-direction:column; gap:8px; max-width:520px; font-family:${EVENT_TOOLTIP_FONT_FAMILY};">
            ${bucket.events.map(buildBucketEventItemHtml).join('')}
        </div>
    `;
};
